import { DocsLayout } from "@/layouts/docs/DocsLayout";
import { DocsMainContent } from "@/scenes/Docs/DocsMainContent/DocsMainContent";
import { DocsLeftContent } from "@/scenes/Docs/DocsLeftContent/DocsLeftContent";
import { DocsRightContent } from "@/scenes/Docs/DocsRightContent/DocsRightContent";
import { NavigationFooter } from "@/components/NavigationFooter/NavigationFooter";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import {
  getMarkdownFilePath,
  getMDXContent,
  getHeadingsFromMarkdown,
  getCategories,
  serializeMarkdown,
} from "@/utils/server/mdx";
import { useLoaderData } from "@remix-run/react";
import { canonicalLinkMeta } from "@/utils/canonicalLinkMeta";
import { getMetaDescriptionFromMarkdown } from "@/utils/getMetaDescriptionFromMarkdown";
import { getTitleTagContent } from "@/utils/getTitleTagContent";

export const loader = async ({ params }: LoaderFunctionArgs) => {
  // params["*"] has a path like this:
  // e.g. /docs/how-to-guides/oauth/using-oauth/setup -> params["*"] = "how-to-guides/oauth/using-oauth/setup"
  const slug = params["*"];

  try {
    const filePath = await getMarkdownFilePath(slug as string);
    const mdxContent = await getMDXContent(filePath);
    const headings = getHeadingsFromMarkdown(mdxContent.content);
    const mdxSource = await serializeMarkdown(mdxContent.content);
    const description = getMetaDescriptionFromMarkdown(mdxContent.content);

    const categories = await getCategories();

    return {
      mdxSource,
      metadata: mdxContent.metadata,
      headings,
      categories,
      description,
    };
  } catch {
    throw new Response("This page does not exist", { status: 404 });
  }
};

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  // Setting fallback title for when data is undefined.
  // However, it won't be used because the ErrorBoundary component at app/routes/_app.tsx will handle the error and render an error title tag.
  const titlekeywords = data ? [data.metadata.title, "Docs"] : ["Docs"];
  return [
    { title: getTitleTagContent(titlekeywords) },
    ...(data?.description ? [{ name: "description", content: data.description }] : []),
    canonicalLinkMeta(location.pathname),
  ];
};

const DocsRoute = () => {
  const { mdxSource, metadata, headings, categories } = useLoaderData<typeof loader>();

  return (
    <DocsLayout
      leftContent={<DocsLeftContent categories={categories} />}
      rightContent={<DocsRightContent headings={headings} meta={metadata} />}
      footer={
        <NavigationFooter
          previous={metadata.previous ?? null}
          previousLink={metadata.previous_link ?? null}
          next={metadata.next ?? null}
          nextLink={metadata.next_link ?? null}
        />
      }
    >
      <DocsMainContent mdxSource={mdxSource} />
    </DocsLayout>
  );
};

export default DocsRoute;
